/**
 * project127 / T102 · P144 T0406 — Harness read seam for **Entity Review** (the ONE loud channel,
 * D-006). The WORD `Decide` is retired (P144 IN-06/T0109): it named the act performed inside the
 * surface, not the surface. The ROUTE and the payload key below keep their names on purpose —
 * they are the API contract (no endpoint was renamed, P144 §🔗), and renaming a wire field to
 * match a UI label is how two names for one thing get created.
 *
 * POST /api/v1/harness/decide-queue — per-entity aggregate of everything the machine
 * brings to the operator, built from REAL rows only (process-gate pending + hermes
 * needs-you; the outreach-drafts lane counts the per-recipient ledger
 * (POST /api/v1/outreach-ledger/recipient et al, migration 000010) and reports
 * wired:false with its reason only where that migration is absent). Reads only — acts stay on each source's EXISTING gate
 * (process-gate approve/reject = processApi.decideReview; never a generic approve).
 */
import AuthUtils from "@/providers/firebase/authUtils";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const DEFAULT_TIMEOUT_MS = 30_000;

async function apiCall<T>(
  path: string,
  options: RequestInit & { timeoutMs?: number } = {},
): Promise<ApiResponse<T>> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const headers = await AuthUtils.getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...fetchOptions,
      headers: { ...headers, "Content-Type": "application/json", ...fetchOptions.headers },
      signal: controller.signal,
    });
    return res.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
}

// ---- decide-queue contract (harness_manager.py:POST /harness/decide-queue) --------
export interface DecideQueueItem {
  item_id: string;
  kind: string;
  title: string;
  detail?: string;
  freshness?: string | null;
  actor?: string | null;
  actions: string[];
  /**
   * P144 T0202 — true when this item waits on a DECISION (approve/reject/run, or a preparation the
   * machine wrote and is holding). False when it only asks to be LOOKED AT.
   *
   * It is declared by the backend, never inferred here from the lane's name (D-144-002): the red
   * number is `Σ items where decision_required`, so a lane whose meaning changes can no longer
   * change the dot silently. Optional on purpose — an older cached payload without it defaults to
   * "no dot", which is the safe direction. */
  decision_required?: boolean;
  deep_link?: string;
  /**
   * P276 T0102 — the process this item is about, and the `MR29` classes it needs but the company
   * has not connected. Present only on the `integration-required` lane; optional so an older
   * cached payload stays valid.
   */
  process_id?: string;
  process_name?: string;
  missing_classes?: string[];
  label?: string;
}

export interface DecideLane {
  lane: string;
  wired: boolean;
  count: number;
  note?: string;
  deep_link?: string;
  error?: string;
  items: DecideQueueItem[];
}

export interface DecideQueue {
  entity_id: string;
  generated_at: string;
  total: number;
  any_due: boolean;
  lanes: DecideLane[];
}

export async function getDecideQueue(
  entityId: string,
  windowDays = 30,
): Promise<ApiResponse<DecideQueue>> {
  return apiCall<DecideQueue>("/api/v1/harness/decide-queue", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, window_days: windowDays }),
  });
}

// ---- portfolio contract (T103 / D-009 — ONE aggregate poll for the company rail) --------
export interface PortfolioEntity {
  entity_id: string;
  name: string;
  total: number;
  process_gate: number;
  /**
   * P144 D-144-019 — how many of `process_gate`'s items carry a recorded first view.
   * The 50% state is derived from this and is only honest where a view is a recorded fact:
   * process reviews have one (`review_views`) and drafts have one since 2026-09-28
   * (`draft_views`, migration 000032), so a lane WITHOUT such a fact can never dim a number.
   */
  process_gate_viewed?: number;
  /** The whole wake lane's item count — unchanged meaning; the two fields below are its split. */
  hermes_wake: number;
  /** P144 D-144-017 — the part of the wake lane that ASKS for a decision (it is the red part). */
  hermes_wake_decisions?: number;
  /** Worth knowing: real signals that ask for no verdict. Counted, named, and NOT red — the
   *  owner must still see the machine working (FTUE implication 13: an idle-looking machine
   *  reads as a dead one). */
  knowing?: number;
  /** Drafts written and waiting on a decision — the email surface's own work (D-144-011). */
  preparation?: number;
  /** How many of `preparation` the reader has already OPENED (`draft_views`). */
  preparation_viewed?: number;
  /**
   * Whether that opened count could be READ AT ALL — false on an environment where migration
   * 000032 is not applied yet. It is a separate field on purpose: `preparation_viewed: 0` means
   * "he opened none of them" only when this is true, and `isQuiet`'s caller must pass 0 as the
   * with-facts figure when it is false. A count may never dim on facts it does not have.
   */
  preparation_view_facts?: boolean;
  error?: string;
}

export interface HarnessPortfolio {
  generated_at: string;
  scope: string;
  total_outstanding: number;
  entities: PortfolioEntity[];
  /** T0197 — the portfolio strip: a FILTERED READ over the same store, carrying only tasks
   *  with something awaiting the owner's verdict. The per-entity list stays the home. */
  strip?: PortfolioStrip;
}

export async function getPortfolio(): Promise<ApiResponse<HarnessPortfolio>> {
  // User token → owner-scoped (MR24); body stays empty so the server resolves the set.
  return apiCall<HarnessPortfolio>("/api/v1/harness/portfolio", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ---- T105 (D-008) — command/ask ledger (the Harness "Direct" surface) --------
// One line in → a durable, audited job card out → honest state → receipts in Audit.
// Status is only ever set by the BE to a provable state; a free-form directive lands
// honest 'waiting_on_you' (the coordinator run path is service-gated by design, D-045).
export type HarnessJobKind = "command" | "ask";
export type HarnessJobStatus = "queued" | "running" | "done" | "failed" | "waiting_on_you";

export interface HarnessJob {
  id: number;
  entity_id: string;
  kind: HarnessJobKind;
  status: HarnessJobStatus;
  command: string;
  bot_id?: string | null;
  bot_call_id?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  error?: string | null;
}

export interface HarnessJobsResponse {
  entity_id: string;
  jobs: HarnessJob[];
}

export async function postCommand(
  entityId: string,
  kind: HarnessJobKind,
  command: string,
  botId?: string,
): Promise<ApiResponse<{ job: HarnessJob }>> {
  // The named-bot path executes synchronously on the BE (MR0t-logged runner), so allow
  // a longer window than the default read timeout.
  return apiCall<{ job: HarnessJob }>("/api/v1/harness/command", {
    method: "POST",
    timeoutMs: 90_000,
    body: JSON.stringify({ entity_id: entityId, kind, command, bot_id: botId ?? null }),
  });
}

export async function getJobs(
  entityId: string,
  limit = 30,
): Promise<ApiResponse<HarnessJobsResponse>> {
  return apiCall<HarnessJobsResponse>("/api/v1/harness/jobs", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, limit }),
  });
}

// ---- T107 (D-005) — pacing config (the wake cadence the workers read) --------
// Stored per-entity in preferences['pacing']; conservative run-safe presets default.
// Autonomy is NOT settable here — it is earned via MAST (displayed read-only).
export type PacingPreset = "steady" | "active" | "close";

export interface HarnessPacing {
  entity_id: string;
  wake_cadence_hours: number;
  preset: PacingPreset | "custom";
  is_default: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}

export const PACING_PRESETS: Record<PacingPreset, { label: string; hours: number; hint: string }> = {
  steady: { label: "Steady", hours: 24, hint: "Wake once a day — the safe default" },
  active: { label: "Active", hours: 12, hint: "Wake twice a day — a raise in motion" },
  close: { label: "Close watch", hours: 6, hint: "Wake every 6h — a critical period" },
};

export async function getPacing(entityId: string): Promise<ApiResponse<HarnessPacing>> {
  return apiCall<HarnessPacing>("/api/v1/harness/pacing", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId }),
  });
}

export async function updatePacing(
  entityId: string,
  input: { preset?: PacingPreset; wake_cadence_hours?: number },
): Promise<ApiResponse<HarnessPacing>> {
  return apiCall<HarnessPacing>("/api/v1/harness/pacing/update", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, ...input }),
  });
}

// ---- recurring tasks (project146 T0185) --------------------------------------
// "What runs on its own." Read from the store the BE owns
// (GET /api/v1/hermes/recurring-tasks). This page never invents a row, and an empty list is a
// statement about the COMPANY, not about the machine — so the empty state says so, with the time
// it was checked.

export interface RecurringTaskVerdict {
  status?: string | null;
  /** The stored outcome in the words the owner reads — `accepted · changed by you · rejected ·
   *  waiting for your call`. Rendered by the BACKEND so the two surfaces cannot drift (T0192). */
  outcome_word?: string | null;
  /** Whether he has OPENED this yet — a stored fact (`review_views`, first view wins), never
   *  inferred from age (T0192). */
  opened?: boolean | null;
  opened_at?: string | null;
  /** RETIRED (T0192): the duplicate column on `kernel_review_queue`. The one source is `status`. */
  verdict?: string | null;
  reviewed_at?: string | null;
  feedback?: string | null;
}

export interface RecurringTaskRow {
  id: string;
  what_runs: string;
  domain?: string | null;
  how_often_hours: number;
  last_run_at?: string | null;
  last_run_call_id?: string | null;
  last_outcome?: string | null;
  /** T0203 — the outcome in the words the owner reads, rendered by the BACKEND so the surface
   *  and any other reader cannot drift on what a term means. */
  last_outcome_word?: string | null;
  next_due?: string | null;
  enabled: boolean;
  trigger_kind: string;
  declared_by: string;
  declared_at?: string | null;
  verdict?: RecurringTaskVerdict | null;
  /** 1 accepted · 0 rejected · **null = nothing has judged it yet** (never 0 for "not judged") */
  was_useful?: number | null;
  /** "watched" = its condition can be evaluated today · "unwatched" = nothing reads its source yet */
  watch_state: "watched" | "unwatched";
  /** T0198 — the gate's scope for this task. `requires_verdict` is the task's own declaration
   *  (it may only RAISE the floor, never lower it); `outcome` says how the last piece of work
   *  actually left. A skipped step reports `without_your_verdict`, which is the earned freedom
   *  the confidence ladder grants — stated plainly, never hidden. */
  gate?: {
    requires_verdict: boolean;
    decided_by: string;
    message: string;
    outcome: "with_your_verdict" | "without_your_verdict";
    word: string;
    gated: boolean;
  } | null;
  /** T0200 — who decided this row, in the owner's words, plus any proposal waiting for his
   *  call. `may_overwrite: false` means the value is his and the harness must propose. */
  decider?: {
    decided_by: string | null;
    decided_at: string | null;
    decided_by_user_id: string | null;
    decided_word: string;
    may_overwrite: boolean;
    open_proposals: Array<Record<string, unknown>>;
    transfer: { inherited: boolean; needs_review: boolean; word: string; message?: string };
  } | null;
}

/** T0201 — one capability the engine documents, as a control the owner operates or a state he
 *  reads. `label: null` means it shows nothing on the surface (the pluggable trigger). */
export interface EngineCapability {
  key: string;
  kind: "control" | "state";
  label: string | null;
  what: string;
  engine: string;
}

/** T0201 — an edge the engine cannot cover, stated so the surface refuses honestly instead of
 *  implying a capability. */
export interface HonestEdge {
  asked_for: string;
  honest_state: string;
  offered_instead: string;
}

/** T0197 — one entry on the portfolio strip: enough to judge whether to open the entity, and
 *  a way back to its home. The strip carries eligibility, not everything. */
export interface PortfolioStripEntry {
  entity_id: string;
  entity_name: string;
  row_id: string | null;
  what_runs: string | null;
  domain: string | null;
  reason: "verdict_waiting" | "opened_not_decided" | null;
  reason_word: string | null;
  last_run_at: string | null;
  next_due: string | null;
  gate?: RecurringTaskRow["gate"];
}

/** T0197 — the portfolio strip: a FILTERED READ over the same store, carrying only tasks with
 *  something awaiting the owner's verdict. The per-entity list stays the home. */
export interface PortfolioStrip {
  entries: PortfolioStripEntry[];
  total_waiting: number;
  shown: number;
  truncated: boolean;
  per_entity: Record<string, number>;
  entities_with_work: number;
  home: string;
  unavailable?: boolean;
}

/** T0201 — the engine's whole capability set, in one call, so a surface cannot render half of
 *  it and imply the rest. */
export interface SurfaceCapabilities {
  controls: EngineCapability[];
  states: EngineCapability[];
  outcomes: Record<string, string>;
  edges: HonestEdge[];
  clock: Record<string, unknown>;
}

/**
 * T0193 — the clock's own liveness. The register can be perfect and still never run, because the
 * tick is what makes it move: `stopped` must never render as a calm company.
 */
export interface RecurringTasksClock {
  available: boolean;
  state: "not_needed" | "never_ticked" | "ticking" | "stopped";
  watched_rows: number;
  threshold_hours: number;
  decided_by: "declared_default" | "owner" | "caller";
  last_tick_at?: string | null;
  last_clock_tick_at?: string | null;
  age_seconds?: number | null;
  tick_count?: number;
  clock_tick_count?: number;
  reason?: string;
}

export interface RecurringTasksCoverageDimension {
  available: boolean;
  items: { key: string; name: string }[];
  covered: number;
  uncovered: { key: string; name: string; needs: string }[];
  reason?: string;
}

/** T0207 — the fourth measure: is every objective and room covered by a declared task, or does it
 *  carry a written "nothing watches this yet"? `dimensions_unreadable` names what cannot be read. */
export interface RecurringTasksCoverage {
  available: boolean;
  entity_id: string;
  dimensions: Record<string, RecurringTasksCoverageDimension>;
  dimensions_covered: string[];
  dimensions_unreadable: string[];
  uncovered_total: number;
  markers: string[];
}

export interface RecurringTasks {
  entity_id: string;
  count: number;
  rows: RecurringTaskRow[];
  /** T0193 — whether the thing that MAKES these run is still running. */
  clock?: RecurringTasksClock;
  /** T0207 — what is not covered by any recurring task, and what cannot be read at all. */
  coverage?: RecurringTasksCoverage;
  /** T0184 — the budget, in the currency the decision named: ONE PREPARATION AT THE OWNER'S GATE.
   *  The lanes are ranked gate > recurring task > event wake, so proactive work pauses at the cap
   *  rather than burying him. `decided_by` distinguishes the declared default from his own cap. */
  budget?: {
    available: boolean;
    waiting?: number;
    cap?: number;
    room_left?: number;
    exhausted?: boolean;
    decided_by?: string;
    reason?: string;
    /** T0195 — the EFFORT measure BESIDE the currency: what the machine spent on its own, in the
     *  counter that already exists (`proactivity_self_initiated`). Reported separately on purpose
     *  — a task that ran and produced nothing moves this and not `waiting`, which is why the
     *  currency is the owner's attention and not the machine's effort. */
    effort?: {
      available: boolean;
      kpi_key?: string | null;
      window_days?: number;
      unattended_runs?: number;
      completed?: number;
      failed?: number;
      by_status?: Record<string, number>;
      reason?: string;
    };
  };
}

export async function getRecurringTasks(entityId: string): Promise<ApiResponse<RecurringTasks>> {
  return apiCall<RecurringTasks>(
    `/api/v1/hermes/recurring-tasks?entity_id=${encodeURIComponent(entityId)}`,
  );
}

/** T0208 — the OWNER'S edit. A refusal comes back as a reason (`cadence_out_of_range`,
 *  `row_not_found`, `no_change`) rather than a silent success, so the surface can say what
 *  happened instead of pretending the change landed. */
export interface RecurringTaskUpdate {
  updated: boolean;
  reason?: string;
  allowed?: string;
  changes?: Record<string, { from: unknown; to: unknown }>;
  decided_by?: string;
  decided_at?: string;
}

export async function updateRecurringTask(
  entityId: string,
  input: { row_id: string; cadence_hours?: number; enabled?: boolean },
): Promise<ApiResponse<RecurringTaskUpdate>> {
  return apiCall<RecurringTaskUpdate>("/api/v1/hermes/recurring-tasks/update", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, ...input }),
  });
}

/** T0206 — THE BIRTH OF A DECLARATION. `process_key` is omitted on purpose: work the company has
 *  committed to but not yet built is declared honestly and reports itself as `not_instantiated`. */
export async function declareRecurringTask(
  entityId: string,
  input: {
    declared_name: string;
    declaration_source: string;
    trigger_kind: string;
    cadence_hours: number;
    process_key?: string;
    domain?: string;
    objective_key?: string;
  },
): Promise<ApiResponse<{ created: boolean; reason?: string; id?: string; decided_by?: string }>> {
  return apiCall("/api/v1/hermes/recurring-tasks/declare", {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId, ...input }),
  });
}

// ---- P276 T0102/T0103 — the integration requirement, at both altitudes ----------------
// A process that cannot run for want of a connection is a gate item at the PROCESS altitude,
// which is the altitude the PO named when he rejected a niche control in a process list: the
// signal belongs where the process lives, not in a configuration surface.
//
// The lane arrives inside `decide-queue` (kind `integration_required`), so the dot needs no
// second poll — and the two altitudes read the SAME lane, so they cannot disagree.

/** The classes a process requires that this company has not connected. */
export interface UnmetRequirement {
  process_id: string;
  process_name: string;
  missing_classes: string[];
  label: string;
}

/** The `integration-required` lane, narrowed out of the queue. `null` when the lane is absent. */
export function integrationLane(queue: DecideQueue | null | undefined): DecideLane | null {
  return (queue?.lanes || []).find((l) => l.lane === "integration-required") ?? null;
}

/**
 * The processes blocked on a connection, from a queue already fetched.
 *
 * Returns `[]` for a lane that is absent or unwired — an honest empty, never a fabricated zero:
 * a lane that could not be read says so in its own `note`, and the caller renders that instead.
 */
export function unmetRequirements(queue: DecideQueue | null | undefined): UnmetRequirement[] {
  const lane = integrationLane(queue);
  if (!lane || !lane.wired) return [];
  return (lane.items || []).map((item) => ({
    process_id: String(item.process_id ?? ""),
    process_name: String(item.process_name ?? item.title ?? ""),
    missing_classes: (item.missing_classes as string[]) || [],
    label: String(item.label ?? item.title ?? ""),
  }));
}
